<template>
  <div class="daily-stats">
    <v-chart ref="chart" autoresize theme="light" :options="chart"/>
    <div class="daily-stats__controls">
      <sw-button
        class="daily-stats__control"
        v-model="parameters.rangeMode"
        :label="$t('component.chart.rangeMode')"
      ></sw-button>
      <div v-if="!parameters.rangeMode">
        <min-sel v-model="parameters.days" @input="daysChange">
          <option :value="14">14 {{ $t('component.chart.days') }}</option>
          <option :value="31">31 {{ $t('component.chart.days') }}</option>
          <option :value="90">90 {{ $t('component.chart.days') }}</option>
          <option :value="180">180 {{ $t('component.chart.days') }}</option>
          <option :value="365">365 {{ $t('component.chart.days') }}</option>
        </min-sel>
      </div>
      <div class="daily-stats__range" v-else>
        <in
          type="date"
          @input="rangeInput"
          class="daily-stats__control"
          v-model="parameters.rangeStart"
          :label="$t('component.chart.rangeStart')"
        ></in>
        <in
          type="date"
          @input="rangeInput"
          class="daily-stats__control"
          v-model="parameters.rangeStop"
          :label="$t('component.chart.rangeStop')"
        ></in>
      </div>
    </div>
  </div>
</template>

<script>
import axios from '@/axios'

export default {
  data() {
    return {
      parameters: {
        days: 14,
        rangeMode: false,
        rangeStart: null,
        rangeStop: null
      },
      chart: {
        title: {
          text: this.$t('view.dashboard.dailyStats')
        },
        textStyle: {
          fontFamily: 'lato'
        },
        tooltip: {
          trigger: 'axis'
        },
        legend: {
          data: [this.$t('component.chart.allUsers'), this.$t('component.chart.uniqueUsers'), this.$t('component.chart.newUsers'), this.$t('component.chart.deletedUsers'), this.$t('component.chart.sent'), this.$t('component.chart.received'), this.$t('component.chart.total')],
          selected: {}
        },
        toolbox: {
          show: true,
          feature: {
            magicType: { show: true, type: ['stack', 'tiled'], title: { stack: this.$t('component.chart.stack'), tiled: this.$t('component.chart.tiled') } },
            saveAsImage: { show: true, title: this.$t('component.chart.saveAsImage') }
          }
        },
        xAxis: {
          data: []
        },
        yAxis: [{
          type: "value",
          name: this.$t('component.chart.users')
        }, {
          type: "value",
          name: this.$t('component.chart.messages')
        }],
        series: [{
          name: this.$t('component.chart.allUsers'),
          type: 'line',
          yAxisIndex: 0,
          data: [],
          color: '#dd7373'
        },
        {
          name: this.$t('component.chart.uniqueUsers'),
          type: 'line',
          yAxisIndex: 0,
          data: [],
          color: '#007cbe'
        },
        {
          name: this.$t('component.chart.newUsers'),
          type: 'line',
          yAxisIndex: 0,
          data: [],
          color: '#ffd23f'
        }, {
          name: this.$t('component.chart.deletedUsers'),
          type: 'line',
          yAxisIndex: 0,
          data: [],
          color: '#3BCEAC'
        }, {
          name: this.$t('component.chart.sent'),
          type: 'bar',
          yAxisIndex: 1,
          stack: 'messages',
          data: [],
          color: '#4C95AA'
        }, {
          name: this.$t('component.chart.received'),
          type: 'bar',
          yAxisIndex: 1,
          stack: 'messages',
          data: [],
          color: '#4DAA57'
        }, {
          name: this.$t('component.chart.total'),
          type: 'bar',
          yAxisIndex: 1,
          data: [],
          color: '#73B5DD'
        }]
      }
    }
  },
  methods: {
    loadDataToChart(daily, messages) {
      const h24 = 24 * 60 * 60 * 1000
      let dates = []
      let sent = []
      let received = []
      let total = []
      let newUsers = []
      let allUsers = []
      let deletedUsers = []
      let uniqueUsers = []
      let lastPushedDate = 0;
      for (let i = 0; i < daily.length; i++) {
        while (i && +new Date(daily[i].date) - lastPushedDate > h24) {
          dates.push(this.formDate(new Date(lastPushedDate + h24)))
          allUsers.push(daily[i - 1].messenger_all_users + daily[i - 1].webchat_all_users)
          uniqueUsers.push(0)
          newUsers.push(0)
          deletedUsers.push(0)
          lastPushedDate = +new Date(lastPushedDate + h24)
        }
        dates.push(this.formDate(new Date(daily[i].date)))
        allUsers.push(daily[i].messenger_all_users + daily[i].webchat_all_users)
        uniqueUsers.push(daily[i].messenger_unique_users + daily[i].webchat_unique_users)
        newUsers.push(daily[i].messenger_new_users + daily[i].webchat_new_users)
        deletedUsers.push(daily[i].messenger_deleted_users + daily[i].webchat_deleted_users)
        lastPushedDate = +new Date(daily[i].date)
      }

      const len = messages.length - 1
      let i = 0
      let actDay = null
      while (i<len) {
        if(actDay) {
          let nextDay = new Date(messages[i].date).setUTCHours(0, 0, 0, 0)
          while(nextDay !== actDay + h24) {
            sent.push(0)
            received.push(0)
            total.push(0)
            actDay += h24
          }
        } else {
          actDay = new Date(messages[i].date).setUTCHours(0, 0, 0, 0)
          let firstDay = new Date(daily[i].date).setUTCHours(0, 0, 0, 0)
          while(actDay !== firstDay && actDay > firstDay) {
            sent.push(0)
            received.push(0)
            total.push(0)
            firstDay += h24
          }
        }
        actDay = new Date(messages[i].date).setUTCHours(0, 0, 0, 0)
        sent.push(0)
        received.push(0)
        total.push(0)
        while (i<len && new Date(messages[i].date).setUTCHours(0, 0, 0, 0) === actDay) {
          sent[sent.length - 1] += messages[i].messenger_messages_outcoming + messages[i].webchat_messages_outcoming
          received[received.length - 1] += messages[i].messenger_messages_incoming + messages[i].webchat_messages_incoming
          i++
        }
        total[total.length - 1] = sent[sent.length - 1] + received[received.length - 1]
      }

      this.chart.xAxis.data = dates
      this.chart.series[0].data = allUsers
      this.chart.series[1].data = uniqueUsers
      this.chart.series[2].data = newUsers
      this.chart.series[3].data = deletedUsers
      this.chart.series[4].data = sent
      this.chart.series[5].data = received
      this.chart.series[6].data = total
    },

    async daysChange(v) {
      try {
        const dataReq = await axios.get(`/api/stats/daily?days=${v}`)
        this.loadDataToChart(dataReq.data.dailyRows, dataReq.data.messagesRows)
      } catch (e) {
        console.error(e)
      }
    },

    async rangeInput() {
      try {
        const start = this.parameters.rangeStart
        const stop = this.parameters.rangeStop
        if (!start || !stop) return
        const dataReq = await axios.get(`/api/stats/daily-range?start=${start}&stop=${stop}`)
        this.loadDataToChart(dataReq.data.dailyRows, dataReq.data.messagesRows)
      } catch (e) {
        console.error(e)
      }
    }
  },
  async created() {
    try {
      this.chart.legend.selected[this.$t('component.chart.total')] = false
      this.chart.legend.selected[this.$t('component.chart.newUsers')] = false
      this.chart.legend.selected[this.$t('component.chart.deletedUsers')] = false
      const rowsReq = await axios.get('/api/stats/daily?days=14')
      this.loadDataToChart(rowsReq.data.dailyRows, rowsReq.data.messagesRows)
    } catch (e) {
      console.error(e)
    }
  }
}
</script>

<style lang="scss" scoped>
.daily-stats {
  @include flowElementShadow();
  background-color: white;
  margin-top: 11px;
  padding: 15px;
  border-radius: 4px;

  &__controls {
    @include alignedFlex(flex-start);
    @include standarizeStatControls();
  }

  &__control {
    margin-right: 20px;
  }

  &__range {
    @include alignedFlex(flex-start);
  }
}
</style>
